"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sparkles, Clock, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Pilgrim Influx & Darshan Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Tirupati experiences massive volume swings aligned with TTD ticket releases and darshan quota windows. We deploy real-time pacing curves and dynamic minimum length-of-stay (MLOS) protocols to maximize RevPAR during high-compression dates.",
  metricLabel: "Strategy focus",
  metricValue: "Quota-linked pricing & weekend length-of-stay fencing"
}, {
  id: 2,
  title: "Alipiri & City-Center Rate Competition",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor rapid intraday tariff adjustments across MakeMyTrip, Agoda, and Booking.com for competing transit and pilgrim hotels. Rule-based yield alerts protect your ADR while capturing high-intent booking traffic early.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & dynamic tariff thresholds"
}, {
  id: 3,
  title: "OTA Margin Leakage & Direct Pilgrim Capture",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Reclaim profit margins absorbed by excessive OTA commissions and unmonitored offline travel desks. We establish strict rate parity across meta-search and drive direct pilgrim bookings through targeted booking incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate control & direct booking incentives"
}];
const seasonalityInsights = [{
  period: "Srivari Brahmotsavam & Major Festivals (Sep - Nov)",
  demand: "Peak Compression",
  strategy: "Implement strict premium pricing, 2-night MLOS, non-refundable booking policies, and direct pre-payment guarantees."
}, {
  period: "School Holidays & Long Weekends (Apr - Jun / Holidays)",
  demand: "High Family Inflow",
  strategy: "Dynamic family-suite yield tiers, meal-inclusive packages, and early booking windows capturing advance darshan ticket holders."
}, {
  period: "Midweek Pilgrim & Transit Windows (Year-round)",
  demand: "Moderate / Value-driven",
  strategy: "Targeted corporate transit rates, local tour operator partnerships, and optimized OTA visibility to maintain consistent base occupancy."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapetirupati" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Tirupati
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Tirupati presents a fast-moving hospitality micro-market governed by pilgrim footfalls, TTD darshan quota calendars, and intense weekend demand compression. Sunil Singh crafts data-driven revenue management strategies tailored specifically for pilgrim hotels, transit stays, and boutique accommodations.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-12 md:mt-16 rounded-lg border border-border bg-card p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-6 border-b border-border/70">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary mb-1">
                <Sparkles className="size-3.5" />
                <span>Demand Seasonality Dynamics</span>
              </div>
              <h3 className="font-serif text-2xl font-bold text-foreground">
                Tirupati Pilgrim & Festival Revenue Cycles
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Targeted pricing frameworks aligned with pilgrim booking velocity to maximize Average Daily Rate (ADR) without sacrificing high occupancy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityInsights.map((season, idx) => <div key={season.period} data-index={idx} className="rounded-md border border-border/60 bg-muted/20 p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="size-4 text-chart-2 shrink-0" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {season.demand}
                    </span>
                  </div>
                  <h4 className="font-semibold text-sm sm:text-base text-foreground mb-3">
                    {season.period}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {season.strategy}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-border/40 flex items-center gap-2 text-xs text-secondary-foreground font-medium">
                  <TrendingUp className="size-3.5 text-chart-2" />
                  <span>Custom Yield Rule</span>
                </div>
              </div>)}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Tirupati hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}